import type {
	FilterRuleModel,
	MetricModel,
	OptimizationIDModel,
} from '@/shared/generated'
import type { TFunction } from 'i18next'

import { useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import {
	createColumnHelper,
	getCoreRowModel,
	getSortedRowModel,
	useReactTable,
} from '@tanstack/react-table'
import { AnimatePresence } from 'framer-motion'
import {
	filter,
	map,
	pipe,
	prop,
	propEq,
	propOr,
	reduce,
	sum,
	values,
} from 'lodash/fp'
import { AlertTriangleIcon, SearchIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	GROUP_VALUE_COLORS,
	MetricGroup,
	MetricGroupValue,
	useRepricingStatisticQuery,
} from '@/entities/optimization-statistic'

import NumberField from '@/shared/components/data-grid/fields/Number/TableCell'
import ProgressRow from '@/shared/components/Progress/ProgressRow'
import { Operators } from '@/shared/components/query-builder'
import { DataTable } from '@/shared/components/tables'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { DeltaValue } from '@/shared/components/ui/delta-value'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
} from '@/shared/components/ui/empty'
import { Content } from '@/shared/components/ui/layout'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '@/shared/components/ui/tabs'
import { useMainSourceProperty } from '@/shared/hooks/data'
import { ampli } from '@/shared/lib/ampli'
import { cn } from '@/shared/lib/utils'
import { formatNumber } from '@/shared/tools/locale'
import { getFiltersQueryString } from '@/shared/tools/location'

import { TabContent } from './OptimizationsSummary'

type RepricingDataType = {
	groupValue: MetricGroupValue
	color: string
} & Record<string, string | number>

export default function ItemsChangedGlobalWidget({
	isLoading: isDataLoading,
	queryParams,
}: {
	queryParams: {
		optimizations: OptimizationIDModel[]
		filters: FilterRuleModel[]
	} | null
	isLoading: boolean
}) {
	const { t } = useTranslation()

	const [activeTab, setActiveTab] = useState<'margin' | 'discount'>('margin')
	const navigate = useNavigate()
	const sourceProperty = useMainSourceProperty()
	const { Locked, Increased, Decreased, NotChanged } = MetricGroupValue
	const {
		data: repricingStatistic,
		isLoading,
		isError,
		error,
	} = useRepricingStatisticQuery(queryParams!, {
		enabled: !!queryParams && !isDataLoading,
		refetchOnMount: true,
		staleTime: 0,
		select: pipe(
			prop('metrics'),
			filter(propEq('group_type', MetricGroup.PriceChangeStatus)),
			reduce((acc: Record<string, any>, item: MetricModel) => {
				const groupValue = item.group_value as MetricGroupValue
				if (!acc[groupValue]) {
					acc[groupValue] = {
						color: GROUP_VALUE_COLORS[groupValue] || '',
						groupValue,
					}
				}
				acc[groupValue]![item.name] = item.value

				return acc
			}, {})
		),
	})
	const total = sum(map(propOr(0, 'product_count'), values(repricingStatistic)))
	const filters = useMemo(
		() => [
			{
				name: 'optimization_group_id',
				operation: Operators.IN as const,
				value: map(
					prop('optimization_group_id'),
					queryParams?.optimizations || []
				),
			},
		],
		[queryParams]
	)
	const rows = useMemo(() => {
		const data = repricingStatistic

		return [
			{
				name: t('status_panel_decreased'),
				products: data?.[Decreased].product_count,
				lines: data?.[Decreased].pricing_line_count,
				color: data?.[Decreased].color,
				filters: [
					{
						name: `diff_${sourceProperty}_pp`,
						operation: Operators.LT as const,
						value: 0,
					},
				],
			},
			{
				name: t('status_panel_increased'),
				products: data?.[Increased].product_count,
				lines: data?.[Increased].pricing_line_count,
				color: data?.[Increased].color,
				filters: [
					{
						name: `diff_${sourceProperty}_pp`,
						operation: Operators.GT as const,
						value: 0,
					},
				],
			},
			{
				name: t('status_panel_price_locked'),
				products: data?.[Locked].product_count,
				lines: data?.[Locked].pricing_line_count,
				color: data?.[Locked].color,
				filters: [
					{
						name: 'is_locked',
						operation: Operators.BL as const,
						value: '1' as const,
					},
				],
			},
			{
				name: t('status_panel_price_not_changed'),
				products: data?.[NotChanged].product_count,
				lines: data?.[NotChanged].pricing_line_count,
				color: data?.[NotChanged].color,
				filters: [
					{
						name: `diff_${sourceProperty}_pp`,
						operation: Operators.EQ as const,
						value: 0,
					},
					{
						name: 'is_locked',
						operation: Operators.BL as const,
						value: '0' as const,
					},
				],
			},
		]
	}, [repricingStatistic])
	if (isLoading || isDataLoading) {
		return <LoadingPlaceholder />
	}
	if (isError) {
		return (
			<Alert variant='destructive' className='w-full'>
				<AlertTriangleIcon />
				<AlertDescription>
					{error.message || t('fatal_error_title')}
				</AlertDescription>
			</Alert>
		)
	}
	const handleRowClick = (row: (typeof rows)[number]) => {
		ampli.optSummaryGlobItemsChangedWidgetClickRow()
		navigate({
			to: `${location.pathname}${getFiltersQueryString([...row.filters, ...filters])}`,
		})
	}

	return (
		!isLoading &&
		!isDataLoading &&
		repricingStatistic && (
			<div className='flex w-full flex-col space-y-3'>
				<div className='space-y-2'>
					{rows.map((row) => (
						<ProgressRow
							key={row.name}
							onClick={() => handleRowClick(row)}
							barClassName={row.color}
							label={row.name}
							relativeValue={(row.products / total) * 100 || 0}
							absoluteValue={formatNumber(row.products) || 0}
							extraValue={formatNumber(row.lines)}
						/>
					))}
				</div>
				<Tabs
					value={activeTab}
					onValueChange={(value) =>
						setActiveTab(value as 'margin' | 'discount')
					}
				>
					<TabsList>
						<TabsTrigger value='margin' data-tab='margin'>
							{t('general_margin', { defaultValue: 'Margin' })}
						</TabsTrigger>
						<TabsTrigger value='discount' data-tab='discount'>
							{t('general_discount', { defaultValue: 'Discount' })}
						</TabsTrigger>
					</TabsList>
					<TabsContent value='margin'>
						<Content className='relative w-full'>
							<AnimatePresence mode='wait'>
								<TabContent key={activeTab}>
									<ItemsChangeTable
										metricType={activeTab}
										data={values(repricingStatistic)}
									/>
								</TabContent>
							</AnimatePresence>
						</Content>
					</TabsContent>
					<TabsContent value='discount'>
						<Content className='relative w-full'>
							<AnimatePresence mode='wait'>
								<TabContent key={activeTab}>
									<ItemsChangeTable
										metricType={activeTab}
										data={values(repricingStatistic)}
									/>
								</TabContent>
							</AnimatePresence>
						</Content>
					</TabsContent>
				</Tabs>
			</div>
		)
	)
}
export const ItemsChangeTable = ({
	metricType,
	data = [],
}: {
	data: RepricingDataType[]
	metricType: string
}) => {
	const { t } = useTranslation()

	const columns = useMemo(() => getColumns(metricType, t) || [], [])
	const table = useReactTable<RepricingDataType>({
		columns,
		data,
		defaultColumn: {
			minSize: 28,
			size: Number.MAX_SAFE_INTEGER,
			maxSize: Number.MAX_SAFE_INTEGER,
		},
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
	})
	if (!data?.length) {
		return (
			<div className='border-base flex max-h-[350px] w-full flex-col overflow-hidden rounded-lg border border-solid py-5'>
				<Empty>
					<EmptyHeader>
						<EmptyMedia variant='icon'>
							<SearchIcon className='text-muted-foreground' />
						</EmptyMedia>
						<EmptyDescription>{t('no_data')}</EmptyDescription>
					</EmptyHeader>
				</Empty>
			</div>
		)
	}

	return (
		<div className='overflow-hidden rounded-lg border'>
			<DataTable compact table={table} getRowClassName={() => 'h-8'} />
		</div>
	)
}

const columnHelper = createColumnHelper<RepricingDataType>()
const getColumns = (metricType: string, t: TFunction) => [
	columnHelper.accessor('groupValue', {
		header: () => t('metric.trend_feature', { defaultValue: 'Trend' }),
		cell: (info) => {
			const key = info.getValue()

			return (
				<div className='items-center space-x-1 text-sm'>
					<span
						className={cn(
							'size-2 shrink-0 rounded-full',
							info.row.original.color
						)}
					/>

					<span>{t(`overview_price_${key}`, { defaultValue: key })}</span>
				</div>
			)
		},
	}),
	columnHelper.accessor(`${metricType}_init`, {
		size: 70,
		header: () => t('current', { defaultValue: 'Current' }),
		cell: (info) => (
			<div className='text-sm'>
				<NumberField value={info.getValue()} />
			</div>
		),
	}),
	columnHelper.accessor(`${metricType}_final`, {
		size: 70,
		header: () => t('new', { defaultValue: 'New' }),
		cell: (info) => (
			<div className='text-sm'>
				<NumberField value={info.getValue()} />
			</div>
		),
	}),
	columnHelper.accessor('margin_final', {
		size: 70,
		header: () => 'Δ',
		cell: (info) => {
			const value =
				Number(info.row.original[`${metricType}_final`]) -
					Number(info.row.original[`${metricType}_init`]) || 0

			return (
				<DeltaValue value={value} className='text-sm'>
					<NumberField value={value} />
				</DeltaValue>
			)
		},
	}),
]

function LoadingPlaceholder() {
	return (
		<div className='flex w-full flex-col items-center space-y-2'>
			<div className='flex max-h-[400px] w-full items-center justify-between'>
				<Skeleton className='h-5 w-20' />
				<Skeleton className='h-5 w-10' />
			</div>
			<div className='flex w-full items-center justify-between'>
				<Skeleton className='h-5 w-24' />
				<Skeleton className='h-5 w-12' />
			</div>
			<div className='flex w-full items-center justify-between'>
				<Skeleton className='h-5 w-28' />
				<Skeleton className='h-5 w-12' />
			</div>
		</div>
	)
}
